import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = "RapidMind AI - Professional AI Tools for Content Creation",
  description = "Transform your ideas into stunning content with RapidMind AI. Generate images, text, and more with our advanced AI tools. Free to start, powerful for creators.",
  keywords = "AI tools, content creation, image generator, AI workspace, rapidmind ai, free ai tools",
  image = "/RapidMind 2025.png",
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = "website",
  author = "RapidMind AI",
  publishedTime,
  modifiedTime,
  section,
  tags
}) => {
  const siteName = "RapidMind AI";
  const twitterHandle = "@rapidmindai";
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Structured data for AI tools/software
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "RapidMind AI",
    "description": description,
    "url": baseUrl,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free tier available"
    },
    "author": {
      "@type": "Organization",
      "name": "RapidMind AI"
    },
    "featureList": [
      "AI Image Generation",
      "Text Content Creation",
      "AI Workspace",
      "Mobile-First Design",
      "Real-time Collaboration"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <link rel="canonical" href={url} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />

      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#3b82f6" />
      <meta name="msapplication-TileColor" content="#3b82f6" />
      <meta name="application-name" content={siteName} />

      {/* Article specific tags (if applicable) */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}
      {section && <meta property="article:section" content={section} />}
      {tags && <meta property="article:tag" content={tags} />}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/RapidMind 2025.png" />
      <link rel="apple-touch-icon" href="/RapidMind 2025.png" />
    </Helmet>
  );
};

export default SEO;
